import buildUrl from 'build-url';
import parseUrl from 'parse-url';
import { getCategoriesUrl, getBrandsUrl, getAllProductsUrl } from 'Client/urlService';
import { LandingRedirectsData } from 'Client/redux/redirect/types';
import findLandingPageRedirect from 'Common/LandingPageRedirectService';
import { DynamicSearchSuggestionBucket, RedirectSuggestions } from 'Client/redux/dynamicSearch/types';

interface RedirectSource {
    titleKey: string;
    suggestionKey: keyof RedirectSuggestions;
    getUrl: (searchValue: DynamicSearchSuggestionBucket) => string;
}

export interface SearchSuggestionRedirectData {
    url: string;
    isRedirect: boolean;
}

// redirects options will be checked in order they are in this array
const redirectSources: RedirectSource[] = [
    { titleKey: 'Static_pages_navigation_title', suggestionKey: 'staticPages', getUrl: ({ urlKey }) => `/${urlKey}` },
    { titleKey: 'Categories', suggestionKey: 'categories', getUrl: ({ urlKey }) => getCategoriesUrl(urlKey) },
    { titleKey: 'Brands', suggestionKey: 'brands', getUrl: ({ urlKey }) => getBrandsUrl(urlKey) },
    { titleKey: 'Products', suggestionKey: 'products', getUrl: ({ title }) => getAllProductsUrl(title) },
];

const getRedirectData = (
    redirectSource: RedirectSource,
    bucketState: DynamicSearchSuggestionBucket,
    keyword: string,
    queryString?: string
    // eslint-disable-next-line max-params
) => {
    const trimmedQuery = keyword.replace(' ', '+');
    const isRedirect = !['products', 'staticPages'].includes(redirectSource.suggestionKey);
    const buildUrlWithKeyword = (url: string, query: string): string => {
        const parsedUrl = parseUrl(url);
        const queryParams = { ...parsedUrl.query, searched: query };
        if (!isRedirect) {
            queryParams.strictSearch = 1;
        }
        return buildUrl('', {
            path: `${parsedUrl.pathname}/`,
            queryParams,
        });
    };

    if (queryString) {
        return {
            url: `${redirectSource.getUrl(bucketState)}${queryString}`,
            isRedirect,
        };
    }

    return {
        url: trimmedQuery
            ? buildUrlWithKeyword(redirectSource.getUrl(bucketState), trimmedQuery)
            : redirectSource.getUrl(bucketState),
        isRedirect,
    };
};

export type LandingPageRedirectOptions = LandingRedirectsData | undefined;

export const getSuggestionRedirect = (
    suggestions: RedirectSuggestions | undefined,
    searchValue: string,
    queryString: string
) => {
    if (suggestions) {
        const redirect = redirectSources.find(({ suggestionKey }) => suggestions[suggestionKey].length > 0);

        if (redirect) {
            return getRedirectData(redirect, suggestions[redirect.suggestionKey][0], searchValue, queryString);
        }
    }

    return {
        url: '',
        isRedirect: false,
    };
};

export const getBestSearchRedirect = (
    suggestions: RedirectSuggestions | undefined,
    landingPageRedirects: LandingPageRedirectOptions,
    searchValue: string
) => {
    const landingPagePath = findLandingPageRedirect(landingPageRedirects, searchValue);
    const url = landingPagePath ? `${landingPagePath}?searched=${searchValue}` : getAllProductsUrl(searchValue);
    if (suggestions) {
        const redirect = redirectSources.find(({ suggestionKey }) => suggestions[suggestionKey].length > 0);

        if (redirect) {
            return getRedirectData(redirect, suggestions[redirect.suggestionKey][0], searchValue);
        }
    }

    return {
        url,
        isRedirect: false,
    };
};

export default { getBestSearchRedirect };
